// Email-safe WYSIWYG editor built on TipTap.
//
// Output is plain HTML using only tags that render consistently across
// email clients (Outlook desktop, Outlook.com, Gmail, Apple Mail):
//   <p> <strong> <em> <u> <a> <ul> <ol> <li> <br>
// Headings, blockquotes, code blocks, horizontal rules, and any tag that
// requires styled-component support are deliberately disabled — those
// render unpredictably in Outlook.

import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import { useEffect } from 'react';
import {
  BoldIcon,
  ItalicIcon,
  UnderlineIcon,
  ListBulletIcon,
  LinkIcon,
} from '@heroicons/react/24/outline';

interface RichTextEditorProps {
  value: string;
  onChange: (html: string) => void;
  minHeight?: number;
}

function ToolbarButton({
  active,
  onClick,
  title,
  children,
}: {
  active?: boolean;
  onClick: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onMouseDown={(e) => {
        // Keep the editor focused — mousedown blurs by default and breaks
        // ranged toggles like "make this selection bold".
        e.preventDefault();
        onClick();
      }}
      title={title}
      className={`p-1.5 rounded text-sm transition-colors ${
        active
          ? 'bg-primary-100 text-primary-700'
          : 'text-gray-600 hover:bg-gray-100'
      }`}
    >
      {children}
    </button>
  );
}

function Toolbar({ editor }: { editor: Editor | null }) {
  if (!editor) return null;

  const promptForLink = () => {
    const existing = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Link URL', existing ?? 'https://');
    if (url === null) return; // cancelled
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    // Add https:// if the user typed a bare domain — most email clients
    // refuse to render <a> tags without a scheme.
    const normalized = /^https?:\/\//i.test(url) ? url : `https://${url}`;
    editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: normalized, target: '_blank', rel: 'noopener noreferrer' })
      .run();
  };

  return (
    <div className="flex items-center gap-1 border-b border-gray-200 px-2 py-1 bg-gray-50 overflow-x-auto">
      <ToolbarButton
        active={editor.isActive('bold')}
        onClick={() => editor.chain().focus().toggleBold().run()}
        title="Bold (⌘B)"
      >
        <BoldIcon className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        active={editor.isActive('italic')}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        title="Italic (⌘I)"
      >
        <ItalicIcon className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        active={editor.isActive('underline')}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        title="Underline (⌘U)"
      >
        <UnderlineIcon className="w-4 h-4" />
      </ToolbarButton>
      <div className="w-px h-5 bg-gray-300 mx-1" />
      <ToolbarButton
        active={editor.isActive('bulletList')}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        title="Bulleted list"
      >
        <ListBulletIcon className="w-4 h-4" />
      </ToolbarButton>
      <ToolbarButton
        active={editor.isActive('orderedList')}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        title="Numbered list"
      >
        <span className="text-xs font-semibold tracking-tight">1.</span>
      </ToolbarButton>
      <div className="w-px h-5 bg-gray-300 mx-1" />
      <ToolbarButton
        active={editor.isActive('link')}
        onClick={promptForLink}
        title="Add or edit link"
      >
        <LinkIcon className="w-4 h-4" />
      </ToolbarButton>
    </div>
  );
}

export function RichTextEditor({
  value,
  onChange,
  minHeight = 160,
}: RichTextEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // Disable tags that render unpredictably in email clients (esp. Outlook):
        heading: false,
        blockquote: false,
        codeBlock: false,
        code: false,
        horizontalRule: false,
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: {
          rel: 'noopener noreferrer',
          target: '_blank',
        },
      }),
    ],
    content: value,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        // Editor styles live in src/styles/index.css under .rich-text-editor.
        class: 'px-3 py-2',
        style: `min-height: ${minHeight}px;`,
      },
    },
  });

  // External value resets (e.g. modal re-opens with new defaults) need to
  // be pushed into the editor since useEditor only reads `content` at mount.
  useEffect(() => {
    if (!editor) return;
    if (value !== editor.getHTML()) {
      editor.commands.setContent(value, { emitUpdate: false });
    }
    // We intentionally don't depend on editor here; depending only on value
    // gives us "external change → push into editor" without ping-pong loops.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <div className="rich-text-editor border border-gray-300 rounded-md overflow-hidden focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500">
      <Toolbar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  );
}
